import { onMounted, onUnmounted, ref } from 'vue'

export type RevealDirection = 'left' | 'right' | 'up' | 'down'

interface UseScrollRevealOptions {
  /** Направление анимации */
  direction?: RevealDirection
  /** Задержка перед анимацией (мс) */
  delay?: number
  /** Порог видимости (0-1) */
  threshold?: number
  /** Расстояние смещения (px) */
  distance?: number
}

/**
 * Composable для анимаций появления элементов при скролле
 */
export function useScrollReveal(options: UseScrollRevealOptions = {}) {
  const {
    direction = 'up',
    delay = 0,
    threshold = 0.1,
    distance = 60
  } = options

  const elementRef = ref<HTMLElement | null>(null)
  const isVisible = ref(false)

  let observer: IntersectionObserver | null = null

  onMounted(() => {
    if (!elementRef.value) return

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible.value) {
            // Если элемент уже в viewport при инициализации, показываем его сразу
            // Иначе - с заданной задержкой
            const elementRect = entry.target.getBoundingClientRect()
            const isInitiallyVisible = elementRect.top >= 0 && elementRect.top <= window.innerHeight
            
            if (isInitiallyVisible && delay === 0) {
              isVisible.value = true
            } else {
              setTimeout(() => {
                isVisible.value = true
              }, delay)
            }
          }
        })
      },
      {
        threshold,
        rootMargin: '0px'
      }
    )

    observer.observe(elementRef.value)
  })

  onUnmounted(() => {
    if (observer) {
      observer.disconnect()
    }
  })

  // CSS переменные для анимации
  const revealStyles = {
    '--reveal-distance': `${distance}px`,
    '--reveal-delay': `${delay}ms`
  }

  return {
    elementRef,
    isVisible,
    revealStyles,
    direction
  }
}

